
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from './CartProvider';
import Toast from './Toast';

const featuredProducts = [
  {
    id: 1,
    name: "Weight Management Capsules",
    category: "Weight Loss",
    price: 2199,
    originalPrice: 2858,
    rating: 4.8,
    reviews: 124,
    image: "/keto1.jpeg",
    benefits: ["Metabolism Boost", "Natural Fat Burn", "Appetite Control"]
  },
  {
    id: 2,
    name: "Joint Pain Relief Oil",
    category: "Joint Pain",
    price: 1599,
    originalPrice: 2079,
    rating: 4.9,
    reviews: 89,
     image: "/joint1.jpeg",
    benefits: ["Reduces Pain", "Improves Mobility", "Natural Healing"]
  },
  {
    id: 3,
    name: "Weight Loss Tablets",
    category: "Weight Loss",
    price: 1999,
    originalPrice: 2599,
    rating: 4.7,
    reviews: 156,
    image: "/gain1.jpeg",
    benefits: ["Reduces Stress", "Improves Sleep", "Boosts Energy"]
  },
  {
    id: 4,
    name: "Fit & Fine Slimming Oil",
    category: "Skin Care",
    price: 1599,
    originalPrice: 2079,
    rating: 4.9,
    reviews: 203,
    image: "/oil1.jpeg",
    benefits: ["Faster Results", "Stimulates Circulation", "Body Toning", "Tones Skin"]
  },
  { 
    id: 5,
    name: "Weight Management Capsules Combo Pack",
    category: "Weight Loss",
    price: 2499,
    originalPrice: 3208,
    rating: 4.8,
    reviews: 124,
    image: "/c3.png",
    benefits: ["Metabolism Boost", "Natural Fat Burn", "Appetite Control"],
  },
];

export default function ProductHighlights() {
  const { state, dispatch } = useCart();
  const [toast, setToast] = useState({ message: '', type: 'success' as const, isVisible: false });
  const [favorites, setFavorites] = useState<number[]>([]);
  
  const isInCart = (productId: number) => {
    return state.items.some(item => item.id === productId);
  };

  const addToCart = (product: typeof featuredProducts[0]) => {
    dispatch({ 
      type: 'ADD_TO_CART', 
      product: {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        category: product.category
      }
    });
    setToast({
      message: `${product.name} added to cart!`,
      type: 'success',
      isVisible: true
    });
  };

  const removeFromCart = (productId: number) => {
    dispatch({
      type: 'REMOVE_FROM_CART',
      productId
    });
    setToast({
      message: 'Item removed from cart!',
      type: 'success',
      isVisible: true
    });
  };

  const toggleFavorite = (productId: number) => {
    setFavorites(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  return (
    <section className="py-10 sm:py-16 lg:py-20 bg-gradient-to-b from-white to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Featured Products
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto px-4">
            Discover our most popular Ayurvedic remedies, carefully crafted to address your wellness needs naturally.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
          {featuredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 group cursor-pointer">
              <div className="relative overflow-hidden rounded-t-2xl bg-gray-100">
                <Link to={`/products/${product.id}`}>
                  <div className="w-full h-48 sm:h-56 lg:h-64 flex items-center justify-center">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                </Link>
                <div className="absolute top-2 sm:top-4 left-2 sm:left-4">
                  <span className="bg-green-600 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                    Save ₹{(product.originalPrice - product.price).toFixed(2)}
                  </span>
                </div>
                <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                  <button 
                    onClick={() => toggleFavorite(product.id)}
                    className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                  >
                    <i className={`${favorites.includes(product.id) ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-600'} text-sm`}></i>
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <div className="mb-2">
                  <span className="text-green-600 text-sm font-medium">{product.category}</span>
                </div>
                
                <Link to={`/products/${product.id}`}>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                    {product.name}
                  </h3>
                </Link>

                <div className="flex items-center mb-3">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-xs sm:text-sm`}></i>
                    ))}
                  </div>
                  <span className="text-gray-600 text-xs sm:text-sm ml-2">
                    {product.rating} ({product.reviews} reviews)
                  </span>
                </div>

                <div className="mb-3 sm:mb-4">
                  {product.benefits.map((benefit, index) => (
                    <span key={index} className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mr-1 sm:mr-2 mb-1">
                      {benefit}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600">₹{product.price}</span>
                    <span className="text-gray-500 line-through text-xs sm:text-sm">₹{product.originalPrice}</span>
                  </div>
                </div>

                <button 
                  onClick={() => isInCart(product.id) ? removeFromCart(product.id) : addToCart(product)}
                  className={`w-full py-2 sm:py-3 rounded-full font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                    isInCart(product.id) 
                      ? 'bg-red-600 text-white hover:bg-red-700' 
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isInCart(product.id) ? 'Remove from Cart' : 'Add to Cart'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link to="/products" className="inline-flex items-center space-x-2 bg-white border-2 border-green-600 text-green-600 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-green-600 hover:text-white transition-all duration-300 whitespace-nowrap">
            <span>View All Products</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />
    </section>
  );
}
